import { Star, Quote, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { FadeUp, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";

// Real reviews — sourced from Metroshine's Google Business Profile.
// Counts shown here visibly back the aggregateRating schema (19 = 18 Google + 1 Yelp).
const featuredReviews = [
  {
    name: "Rosario G.",
    date: "Mar 2025",
    source: "Google",
    text: "Beautiful work done on my Lamborghini Urus, would definitely recommend to anyone local looking for a professional detail.",
  },
  {
    name: "Robbie B.",
    date: "Feb 2025",
    source: "Google",
    text: "My car looks brand new — the paint is gleaming, the interior smells fresh, and every tiny nook and cranny is spotless.",
  },
  {
    name: "Kingston V.",
    date: "Jan 2025",
    source: "Google",
    text: "MetroShine did a great job on my vehicle. I had a bad mess in the car and they didn't judge but just got the job done in a timely manner. Highly recommend.",
  },
  {
    name: "Morgan V.",
    date: "Dec 2024",
    source: "Google",
    text: "Detailer communicated clearly about price and was very prompt on scheduling and following up. His work is crisp and the car looks great!",
  },
  {
    name: "Seif H.",
    date: "Nov 2024",
    source: "Google",
    text: "Amazing wash — not a single spec of dirt or water or stain was found on the car inside or outside. Absolutely recommend.",
  },
  {
    name: "Faisal K.",
    date: "Oct 2024",
    source: "Google",
    text: "Great customer service and my car was spotless after so long of not washing it. They really care about the customer's car.",
  },
];

const GOOGLE_URL = "https://maps.google.com?q=MetroShine&ftid=0x20aa3a091ea792f:0x613f85e03407b5a1&entry=gps";
const YELP_URL = "https://www.yelp.com/biz/metroshine-vienna";

interface Props {
  heading?: string;
  eyebrow?: string;
}

const ReviewsHighlight = ({ heading = "What clients are saying", eyebrow = "5.0 ★ · 19 Reviews" }: Props) => {
  return (
    <section className="section-padding bg-background relative overflow-hidden">
      <div className="absolute inset-0 mesh-gradient opacity-50" />
      <div className="container-luxury relative">
        <FadeUp className="text-center mb-12">
          <span className="badge-shimmer mb-4 inline-flex">{eyebrow}</span>
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
            {heading.split(" ").slice(0, -1).join(" ")} <span className="gold-gradient-text">{heading.split(" ").slice(-1)}</span>
          </h2>
          <div className="flex items-center justify-center gap-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="fill-primary text-primary" size={18} />
            ))}
            <span className="text-muted-foreground text-sm ml-2">5.0 average · 18 Google + 1 Yelp</span>
          </div>
        </FadeUp>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {featuredReviews.map((r, i) => (
            <StaggerItem key={i}>
              <motion.article className="luxury-card-premium rounded-xl h-full flex flex-col" whileHover={{ y: -6 }}>
                <div className="flex items-center justify-between mb-3">
                  <div role="img" className="flex gap-1" aria-label="5 out of 5 stars">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="text-primary fill-primary" size={13} />
                    ))}
                  </div>
                  <span className="text-[10px] tracking-wider uppercase text-primary/70">{r.source}</span>
                </div>
                <Quote size={18} className="text-primary/30 mb-2" />
                <p className="text-muted-foreground text-sm leading-relaxed mb-4 flex-1">"{r.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border/30">
                  <div className="w-8 h-8 rounded-full gold-gradient-bg flex items-center justify-center text-primary-foreground font-display font-bold text-xs">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-display font-semibold text-sm text-foreground leading-none">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{r.date}</p>
                  </div>
                </div>
              </motion.article>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <FadeUp className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <a
            href={GOOGLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-primary/30 text-foreground px-6 py-3 text-sm font-semibold tracking-wider rounded-xl hover:border-primary/60 transition-all"
          >
            See all 18 Google reviews <ArrowRight size={14} />
          </a>
          <a
            href={YELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-primary/30 text-foreground px-6 py-3 text-sm font-semibold tracking-wider rounded-xl hover:border-primary/60 transition-all"
          >
            View on Yelp <ArrowRight size={14} />
          </a>
        </FadeUp>
      </div>
    </section>
  );
};

export default ReviewsHighlight;
